import { useEffect, useState } from "react";

import { Plus, Save, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CustomFieldsEditor } from "@/features/servers/components/CustomFieldsEditor";
import {
  useCenterInfra,
  useUpsertCenterInfra,
} from "@/features/servers/hooks/useCenterInfra";
import type { IContact, ICustomField, IInfraAddress } from "@/features/servers/schemas/servers";
import { formatInfraAddress } from "@/features/servers/utils/address";
import type { User } from "@/features/users/schemas/user";
import { getUserFullName } from "@/features/users/utils/user-utils";

interface CenterInfraHeaderProps {
  institutionUid: string;
  user: User | null | undefined;
}

const EMPTY_ADDRESS: IInfraAddress = {
  street: "",
  city: "",
  state: "",
  country: "",
  zip: "",
};

export function CenterInfraHeader({
  institutionUid,
  user,
}: CenterInfraHeaderProps) {
  const { data: infra } = useCenterInfra(institutionUid);
  const upsert = useUpsertCenterInfra(institutionUid);

  const [address, setAddress] = useState<IInfraAddress>(EMPTY_ADDRESS);
  const [contacts, setContacts] = useState<IContact[]>([]);
  const [publicIps, setPublicIps] = useState<string[]>([]);
  const [customFields, setCustomFields] = useState<ICustomField[]>([]);

  useEffect(() => {
    if (!infra) return;
    setAddress({
      street: infra.address?.street ?? "",
      city: infra.address?.city ?? "",
      state: infra.address?.state ?? "",
      country: infra.address?.country ?? "",
      zip: infra.address?.zip ?? "",
    });
    setContacts(infra.contacts ?? []);
    setPublicIps(infra.publicIps ?? []);
    setCustomFields(infra.customFields ?? []);
  }, [infra]);

  const profileAddress = user?.address
    ? [user.address.street, user.address.city, user.address.state]
        .filter(Boolean)
        .join(", ")
    : null;

  const handleSave = async () => {
    try {
      await upsert.mutateAsync({
        address: {
          street: address.street?.trim() || undefined,
          city: address.city?.trim() || undefined,
          state: address.state?.trim() || undefined,
          country: address.country?.trim() || undefined,
          zip: address.zip?.trim() || undefined,
        },
        contacts: contacts.filter((c) => c.name.trim()),
        publicIps: publicIps.map((ip) => ip.trim()).filter(Boolean),
        customFields: customFields.filter((f) => f.key.trim()),
      });
      toast.success("Cabecera guardada");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Error al guardar");
    }
  };

  const setAddressField = (key: keyof IInfraAddress, value: string) => {
    setAddress((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <Card className="gap-3 py-4 shadow-none">
      <CardHeader className="px-4 pb-0 border-b [.border-b]:pb-3">
        <CardTitle className="text-base">Datos del centro</CardTitle>
        <CardDescription className="text-xs">
          {[user ? getUserFullName(user) : null, user?.email, formatInfraAddress(address) ?? "Sin dirección de ficha"]
            .filter(Boolean)
            .join(" · ")}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5 px-4 pt-3">
        <div className="space-y-2">
          <Label>Dirección (ficha infra)</Label>
          <p className="text-xs text-muted-foreground">
            Independiente del perfil del centro
            {profileAddress ? ` · perfil: ${profileAddress}` : ""}.
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            <Input
              className="sm:col-span-2"
              placeholder="Calle / dirección"
              value={address.street ?? ""}
              onChange={(e) => setAddressField("street", e.target.value)}
            />
            <Input
              placeholder="Ciudad"
              value={address.city ?? ""}
              onChange={(e) => setAddressField("city", e.target.value)}
            />
            <Input
              placeholder="Estado / provincia"
              value={address.state ?? ""}
              onChange={(e) => setAddressField("state", e.target.value)}
            />
            <Input
              placeholder="País"
              value={address.country ?? ""}
              onChange={(e) => setAddressField("country", e.target.value)}
            />
            <Input
              placeholder="Código postal"
              value={address.zip ?? ""}
              onChange={(e) => setAddressField("zip", e.target.value)}
            />
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Contactos IT</Label>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="cursor-pointer"
              onClick={() =>
                setContacts((prev) => [
                  ...prev,
                  { name: "", role: "", phone: "", email: "" },
                ])
              }
            >
              <Plus className="h-3.5 w-3.5 mr-1" aria-hidden />
              Contacto
            </Button>
          </div>
          {contacts.length === 0 ? (
            <p className="text-xs text-muted-foreground">Sin contactos.</p>
          ) : (
            <div className="space-y-2">
              {contacts.map((c, index) => (
                <div
                  key={index}
                  className="grid gap-2 rounded-md border p-3 sm:grid-cols-2"
                >
                  <Input
                    placeholder="Nombre"
                    value={c.name}
                    onChange={(e) =>
                      setContacts((prev) =>
                        prev.map((item, i) =>
                          i === index ? { ...item, name: e.target.value } : item
                        )
                      )
                    }
                  />
                  <Input
                    placeholder="Rol"
                    value={c.role ?? ""}
                    onChange={(e) =>
                      setContacts((prev) =>
                        prev.map((item, i) =>
                          i === index ? { ...item, role: e.target.value } : item
                        )
                      )
                    }
                  />
                  <Input
                    placeholder="Teléfono"
                    value={c.phone ?? ""}
                    onChange={(e) =>
                      setContacts((prev) =>
                        prev.map((item, i) =>
                          i === index ? { ...item, phone: e.target.value } : item
                        )
                      )
                    }
                  />
                  <div className="flex gap-2">
                    <Input
                      placeholder="Email"
                      value={c.email ?? ""}
                      onChange={(e) =>
                        setContacts((prev) =>
                          prev.map((item, i) =>
                            i === index
                              ? { ...item, email: e.target.value }
                              : item
                          )
                        )
                      }
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="size-9 cursor-pointer"
                      aria-label="Eliminar contacto"
                      onClick={() =>
                        setContacts((prev) => prev.filter((_, i) => i !== index))
                      }
                    >
                      <Trash2 className="h-4 w-4 text-destructive" aria-hidden />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>IPs públicas</Label>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="cursor-pointer"
              onClick={() => setPublicIps((prev) => [...prev, ""])}
            >
              <Plus className="h-3.5 w-3.5 mr-1" aria-hidden />
              IP
            </Button>
          </div>
          {publicIps.length === 0 ? (
            <p className="text-xs text-muted-foreground">Sin IPs públicas.</p>
          ) : (
            <div className="space-y-2">
              {publicIps.map((ip, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    placeholder="186.167.81.210"
                    value={ip}
                    onChange={(e) =>
                      setPublicIps((prev) =>
                        prev.map((item, i) =>
                          i === index ? e.target.value : item
                        )
                      )
                    }
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-9 cursor-pointer"
                    aria-label="Eliminar IP"
                    onClick={() =>
                      setPublicIps((prev) => prev.filter((_, i) => i !== index))
                    }
                  >
                    <Trash2 className="h-4 w-4 text-destructive" aria-hidden />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        <CustomFieldsEditor fields={customFields} onChange={setCustomFields} />

        <div className="flex justify-end border-t pt-3">
          <Button
            className="cursor-pointer"
            onClick={() => void handleSave()}
            disabled={upsert.isPending}
          >
            <Save className="h-4 w-4 mr-2" aria-hidden />
            {upsert.isPending ? "Guardando…" : "Guardar cabecera"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
